import { useCallback, useEffect, useRef, useState } from "react";
import {
  COLOR_ACCENT,
  COLOR_BG,
  COLOR_TEXT,
  RADIUS_PANEL,
  Z_DRAG,
} from "../ui/tokens.ts";
import { MESH_IMPORT_DISPLAY } from "./meshImportFormats.ts";
import { useStore } from "./store.ts";
import { useFileLoader } from "./useFileLoader.ts";
// Note: The loading overlay is now rendered globally by <LoadingOverlay />
// in App.tsx, so FileDropZone no longer handles that concern.

/**
 * Root wrapper that provides drag-and-drop file loading and the iPad
 * Safari focus-sink.
 *
 * Children (typically <SceneView />) are rendered inside the drop zone.
 */
export function FileDropZone({ children }: { children: React.ReactNode }) {
  const [dragging, setDragging] = useState(false);
  const handleFile = useFileLoader();

  // iPad Safari won't dispatch keyboard events until a real input element
  // has been focused at least once. We keep a hidden, read-only input that
  // we focus on mount and reclaim on pointer interactions. `readOnly`
  // prevents the on-screen keyboard from appearing.
  const focusSinkRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    focusSinkRef.current?.focus({ preventScroll: true });
  }, []);

  // Warn on page close with unsaved changes
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (useStore.getState().sceneDirty) {
        e.preventDefault();
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, []);

  const claimFocus = useCallback((e: React.PointerEvent) => {
    const target = e.target as HTMLElement;
    const tag = target?.tagName;
    if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
    focusSinkRef.current?.focus({ preventScroll: true });
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragging(false);
      const file = e.dataTransfer.files[0];
      if (file) handleFile(file);
    },
    [handleFile],
  );

  return (
    <div
      onPointerDown={claimFocus}
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      style={{
        width: "100%",
        height: "100%",
        background: COLOR_BG,
        color: COLOR_TEXT,
        fontFamily: "-apple-system, 'SF Pro Display', system-ui, sans-serif",
        position: "relative",
        userSelect: "none",
        WebkitUserSelect: "none",
      }}
    >
      {/* Hidden focus sink — primes iPad Safari keyboard event dispatch */}
      <input
        ref={focusSinkRef}
        readOnly
        tabIndex={-1}
        aria-hidden
        style={{
          position: "absolute",
          opacity: 0,
          width: 0,
          height: 0,
          overflow: "hidden",
          pointerEvents: "none",
        }}
      />

      {/* Drag overlay */}
      {dragging && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: Z_DRAG,
            background: "rgba(14,14,16,0.85)",
            border: `2px dashed rgba(90,160,232,0.5)`,
            borderRadius: RADIUS_PANEL,
            margin: 8,
          }}
        >
          <span style={{ fontSize: 16, color: COLOR_ACCENT }}>
            {`Drop .blend, mesh file (${MESH_IMPORT_DISPLAY}), or image file`}
          </span>
        </div>
      )}

      {children}
    </div>
  );
}
